import React from 'react';

type ToolResultAction = {
  type: 'navigate' | 'send_message' | 'prefill';
  label: string;
  route?: string;
  message?: string;
  value?: string;
};

interface ToolResult {
  success?: boolean;
  action?: string;
  message?: string;
  source?: string;
  confidence?: number;
  actions?: ToolResultAction[];
  metrics?: Record<string, any>;
  at_risk_deals?: Array<{
    title: string;
    value: string;
    stage: string;
    risk: string;
    action: string;
  }>;
  overdue_details?: Array<{
    title: string;
    project_name: string;
    days_overdue: number;
    assignee: string;
  }>;
  insights?: string[];
  data?: Record<string, any>;
  meeting_details?: Record<string, any>;
  next_steps?: string[];
  [key: string]: any;
}

interface ToolResultCardProps {
  result: ToolResult;
  onAction?: (action: ToolResultAction) => void;
}

const MetricCard: React.FC<{ label: string; value: any; color?: string }> = ({ 
  label, 
  value, 
  color = 'blue' 
}) => {
  const colorClasses = {
    blue: 'bg-blue-50 border-blue-200',
    green: 'bg-green-50 border-green-200',
    yellow: 'bg-yellow-50 border-yellow-200',
    red: 'bg-red-50 border-red-200',
    purple: 'bg-purple-50 border-purple-200'
  };

  return (
    <div className={`p-3 border rounded-lg ${colorClasses[color as keyof typeof colorClasses] || colorClasses.blue}`}>
      <div className="text-xs text-gray-600 mb-1">{label}</div>
      <div className="text-xl font-bold text-gray-900">{value}</div>
    </div>
  );
};

const ToolResultCard: React.FC<ToolResultCardProps> = ({ result, onAction }) => {
  const metadata = (
    <div className="flex flex-wrap items-center gap-2 text-[11px] text-gray-500">
      {result.source && (
        <span className="rounded-full bg-gray-100 px-2 py-0.5">Source: {result.source}</span>
      )}
      {typeof result.confidence === 'number' && (
        <span className="rounded-full bg-blue-50 px-2 py-0.5 text-blue-700">
          Confidence: {(result.confidence * 100).toFixed(0)}%
        </span>
      )}
    </div>
  );

  const actionButtons = result.actions && result.actions.length > 0 && (
    <div className="mt-4 flex flex-wrap gap-2">
      {result.actions.map((action, idx) => (
        <button
          key={`${action.label}-${idx}`}
          type="button"
          onClick={() => onAction?.(action)}
          className="rounded-full border border-gray-200 bg-white px-3 py-1 text-xs font-medium text-gray-700 shadow-sm transition hover:border-blue-200 hover:text-blue-700"
        >
          {action.label}
        </button>
      ))}
    </div>
  );

  if (!result.success) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex items-start gap-2">
          <span className="text-xl">❌</span>
          <div>
            <h4 className="font-semibold text-red-900">{result.message || 'Error'}</h4>
            {result.note && <p className="text-sm text-red-700 mt-1">{result.note}</p>}
          </div>
        </div>
        {metadata}
        {actionButtons}
      </div>
    );
  }

  // CRM Report Card
  if (result.action === 'get_crm_report' && result.metrics) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
        <div className="flex items-center gap-2 mb-4">
          <span className="text-2xl">📊</span>
          <h4 className="font-semibold text-gray-900">Pipeline Overview</h4>
        </div>
        
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
          <MetricCard label="Total Deals" value={result.metrics.total_deals} color="blue" />
          <MetricCard label="Pipeline Value" value={result.metrics.pipeline_value} color="green" />
          <MetricCard label="Weighted Forecast" value={result.metrics.weighted_forecast} color="purple" />
          <MetricCard label="Avg Deal Size" value={result.metrics.average_deal_size} color="blue" />
          <MetricCard label="Conversion Rate" value={result.metrics.conversion_rate} color="green" />
        </div>

        {result.at_risk_deals && result.at_risk_deals.length > 0 && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <h5 className="font-semibold text-red-900 mb-3 flex items-center gap-2">
              <span>⚠️</span>
              At-Risk Deals ({result.at_risk_deals.length})
            </h5>
            <div className="space-y-2">
              {result.at_risk_deals.map((deal, idx) => (
                <div key={idx} className="bg-white p-2 rounded border border-red-100">
                  <div className="font-medium text-sm">{deal.title}</div>
                  <div className="text-xs text-gray-600 mt-1">
                    {deal.value} • {deal.stage} • {deal.risk}
                  </div>
                  <div className="text-xs text-blue-600 mt-1">→ {deal.action}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {result.insights && result.insights.length > 0 && (
          <div className="mt-4 space-y-2">
            {result.insights.map((insight: any, idx: number) => (
              <div key={idx} className="flex items-start gap-2 text-sm">
                <span className="text-blue-500 mt-0.5">💡</span>
                <div>
                  <div className="font-medium">{insight.title}</div>
                  <div className="text-gray-600 text-xs mt-0.5">{insight.description}</div>
                </div>
              </div>
            ))}
          </div>
        )}
        {metadata}
        {actionButtons}
      </div>
    );
  }

  // Project Management Report Card
  if (result.action === 'get_project_management_report' && result.metrics) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
        <div className="flex items-center gap-2 mb-4">
          <span className="text-2xl">📋</span>
          <h4 className="font-semibold text-gray-900">Project Management Overview</h4>
        </div>
        
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
          <MetricCard label="Active Projects" value={result.metrics.active_projects} color="blue" />
          <MetricCard label="Total Tasks" value={result.metrics.total_tasks} color="purple" />
          <MetricCard 
            label="Overdue Tasks" 
            value={result.metrics.overdue_tasks} 
            color={result.metrics.overdue_tasks > 0 ? 'red' : 'green'} 
          />
          <MetricCard label="Due Today" value={result.metrics.due_today} color="yellow" />
          <MetricCard label="Due This Week" value={result.metrics.due_this_week} color="blue" />
        </div>

        {result.overdue_details && result.overdue_details.length > 0 && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <h5 className="font-semibold text-red-900 mb-3 flex items-center gap-2">
              <span>🚨</span>
              Overdue Tasks Requiring Attention
            </h5>
            <div className="space-y-2">
              {result.overdue_details.map((task, idx) => (
                <div key={idx} className="bg-white p-2 rounded border border-red-100">
                  <div className="font-medium text-sm">{task.title}</div>
                  <div className="text-xs text-gray-600 mt-1">
                    Project: {task.project_name} • {task.days_overdue} days overdue • {task.assignee}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {result.insights && result.insights.length > 0 && (
          <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <h5 className="font-medium text-blue-900 mb-2">Insights</h5>
            <ul className="space-y-1">
              {result.insights.map((insight: string, idx: number) => (
                <li key={idx} className="text-sm text-blue-800 flex items-start gap-2">
                  <span>•</span>
                  <span>{insight}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
        {metadata}
        {actionButtons}
      </div>
    );
  }

  // SEO Analytics Card
  if ((result.action === 'get_seo_analytics' || result.action === 'generate_seo_report') && result.data) {
    const data = result.data || {};
    return (
      <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
        <div className="flex items-center gap-2 mb-4">
          <span className="text-2xl">📈</span>
          <h4 className="font-semibold text-gray-900">SEO Analytics</h4>
        </div>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          <MetricCard label="Total Clicks" value={data.total_clicks?.toLocaleString()} color="green" />
          <MetricCard label="Impressions" value={data.total_impressions?.toLocaleString()} color="blue" />
          <MetricCard label="Avg CTR" value={data.avg_ctr} color="purple" />
          <MetricCard label="Avg Position" value={data.avg_position} color="yellow" />
        </div>

        {result.insights && result.insights.length > 0 && (
          <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
            <h5 className="font-medium text-yellow-900 mb-2">Recommendations</h5>
            <ul className="space-y-1">
              {result.insights.map((insight: string, idx: number) => (
                <li key={idx} className="text-sm text-yellow-800 flex items-start gap-2">
                  <span>→</span>
                  <span>{insight}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {data.date_range && (
          <div className="mt-3 text-xs text-gray-500 text-center">
            Data: {data.date_range}
          </div>
        )}
        {metadata}
        {actionButtons}
      </div>
    );
  }

  // Meeting Booking Card
  if (result.action === 'book_meeting' && result.meeting_details) {
    return (
      <div className="bg-white border border-green-200 rounded-lg p-4 shadow-sm">
        <div className="flex items-center gap-2 mb-4">
          <span className="text-2xl">✅</span>
          <h4 className="font-semibold text-green-900">Meeting Booked Successfully</h4>
        </div>
        
        <div className="space-y-2 mb-4">
          <div className="flex items-center gap-2">
            <span className="text-gray-600 text-sm w-20">Attendee:</span>
            <span className="font-medium">{result.meeting_details.attendee}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-gray-600 text-sm w-20">Email:</span>
            <span className="font-medium">{result.meeting_details.email}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-gray-600 text-sm w-20">Time:</span>
            <span className="font-medium">{result.meeting_details.time}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-gray-600 text-sm w-20">Duration:</span>
            <span className="font-medium">{result.meeting_details.duration}</span>
          </div>
        </div>

        {result.next_steps && result.next_steps.length > 0 && (
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <h5 className="font-medium text-blue-900 mb-2 text-sm">Next Steps</h5>
            <ul className="space-y-1">
              {result.next_steps.map((step: string, idx: number) => (
                <li key={idx} className="text-sm text-blue-800 flex items-start gap-2">
                  <span>{idx + 1}.</span>
                  <span>{step}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
        {metadata}
        {actionButtons}
      </div>
    );
  }

  // Supplier Report Card
  if ((result.action === 'get_supplier_report' || result.action === 'get_operations_report') && result.metrics) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
        <div className="flex items-center gap-2 mb-4">
          <span className="text-2xl">📦</span>
          <h4 className="font-semibold text-gray-900">Supplier CRM</h4>
        </div>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          <MetricCard label="Total Suppliers" value={result.metrics.total_suppliers} color="blue" />
          <MetricCard label="Active" value={result.metrics.active_suppliers} color="green" />
          <MetricCard label="Onboarded" value={result.metrics.onboarded_suppliers} color="purple" />
          <MetricCard label="Catalog Items" value={result.metrics.catalog_items} color="yellow" />
        </div>

        {result.top_suppliers && result.top_suppliers.length > 0 && (
          <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <h5 className="font-semibold text-blue-900 mb-3">Top Suppliers</h5>
            <div className="space-y-2">
              {result.top_suppliers.map((supplier: any, idx: number) => (
                <div key={idx} className="bg-white p-2 rounded border border-blue-100">
                  <div className="font-medium text-sm">{supplier.name}</div>
                  <div className="text-xs text-gray-600 mt-1">
                    {supplier.category} • {supplier.items_count} items
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {result.insights && result.insights.length > 0 && (
          <div className="mt-4 p-3 bg-gray-50 border border-gray-200 rounded-lg">
            <h5 className="font-medium text-gray-900 mb-2">Insights</h5>
            <ul className="space-y-1">
              {result.insights.map((insight: string, idx: number) => (
                <li key={idx} className="text-sm text-gray-700 flex items-start gap-2">
                  <span>•</span>
                  <span>{insight}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
        {metadata}
        {actionButtons}
      </div>
    );
  }

  // Investor Report Card
  if (result.action === 'get_investor_report' && result.metrics) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
        <div className="flex items-center gap-2 mb-4">
          <span className="text-2xl">💰</span>
          <h4 className="font-semibold text-gray-900">Investor CRM</h4>
        </div>
        
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
          <MetricCard label="Total Investors" value={result.metrics.total_investors} color="blue" />
          <MetricCard label="Active Opportunities" value={result.metrics.active_opportunities} color="green" />
          <MetricCard label="Target Investment" value={result.metrics.total_target_investment} color="purple" />
        </div>

        {result.top_investors && result.top_investors.length > 0 && (
          <div className="mt-4 p-3 bg-purple-50 border border-purple-200 rounded-lg">
            <h5 className="font-semibold text-purple-900 mb-3">Active Investors</h5>
            <div className="space-y-2">
              {result.top_investors.map((investor: any, idx: number) => (
                <div key={idx} className="bg-white p-2 rounded border border-purple-100">
                  <div className="font-medium text-sm">{investor.name}</div>
                  <div className="text-xs text-gray-600 mt-1">
                    {investor.type} • {investor.stage} • {investor.target_amount}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {result.insights && result.insights.length > 0 && (
          <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <h5 className="font-medium text-blue-900 mb-2">Insights</h5>
            <ul className="space-y-1">
              {result.insights.map((insight: string, idx: number) => (
                <li key={idx} className="text-sm text-blue-800 flex items-start gap-2">
                  <span>→</span>
                  <span>{insight}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
        {metadata}
        {actionButtons}
      </div>
    );
  }

  // Finance Report Card
  if (result.action === 'get_finance_report' && result.metrics) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
        <div className="flex items-center gap-2 mb-4">
          <span className="text-2xl">💵</span>
          <h4 className="font-semibold text-gray-900">Financial Forecast</h4>
          {result.scenario && (
            <span className="ml-auto text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
              {result.scenario} scenario
            </span>
          )}
        </div>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          <MetricCard label="Revenue" value={result.metrics.total_revenue} color="green" />
          <MetricCard label="Expenses" value={result.metrics.total_expenses} color="red" />
          <MetricCard label="Net Profit" value={result.metrics.net_profit} color="blue" />
          <MetricCard label="Margin" value={result.metrics.profit_margin} color="purple" />
        </div>

        {result.revenue_breakdown && result.revenue_breakdown.length > 0 && (
          <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
            <h5 className="font-semibold text-green-900 mb-3">Revenue Streams</h5>
            <div className="space-y-2">
              {result.revenue_breakdown.map((stream: any, idx: number) => (
                <div key={idx} className="bg-white p-2 rounded border border-green-100 flex justify-between items-center">
                  <div>
                    <div className="font-medium text-sm">{stream.name}</div>
                    <div className="text-xs text-gray-600">{stream.type}</div>
                  </div>
                  <div className="font-semibold text-green-700">{stream.projected}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {result.insights && result.insights.length > 0 && (
          <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <h5 className="font-medium text-blue-900 mb-2">Financial Insights</h5>
            <ul className="space-y-1">
              {result.insights.map((insight: string, idx: number) => (
                <li key={idx} className="text-sm text-blue-800 flex items-start gap-2">
                  <span>💡</span>
                  <span>{insight}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
        {metadata}
        {actionButtons}
      </div>
    );
  }

  // Staff Report Card
  if (result.action === 'get_staff_report' && result.metrics) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
        <div className="flex items-center gap-2 mb-4">
          <span className="text-2xl">👥</span>
          <h4 className="font-semibold text-gray-900">Staff Management</h4>
        </div>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          <MetricCard label="Total Staff" value={result.metrics.total_staff} color="blue" />
          <MetricCard label="Active" value={result.metrics.active_staff} color="green" />
          <MetricCard label="Pending Holidays" value={result.metrics.pending_holidays} color="yellow" />
          <MetricCard label="Shifts Scheduled" value={result.metrics.total_shifts_scheduled} color="purple" />
        </div>

        {result.pending_requests && result.pending_requests.length > 0 && (
          <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
            <h5 className="font-semibold text-yellow-900 mb-3 flex items-center gap-2">
              <span>📅</span>
              Pending Holiday Requests ({result.pending_requests.length})
            </h5>
            <div className="space-y-2">
              {result.pending_requests.slice(0, 5).map((request: any, idx: number) => (
                <div key={idx} className="bg-white p-2 rounded border border-yellow-100">
                  <div className="text-xs text-gray-600">
                    {request.start_date} to {request.end_date} • {request.days} days • {request.type}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {result.insights && result.insights.length > 0 && (
          <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <h5 className="font-medium text-blue-900 mb-2">HR Insights</h5>
            <ul className="space-y-1">
              {result.insights.map((insight: string, idx: number) => (
                <li key={idx} className="text-sm text-blue-800 flex items-start gap-2">
                  <span>•</span>
                  <span>{insight}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
        {metadata}
        {actionButtons}
      </div>
    );
  }

  // Generic fallback for other tool results
  return (
    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
      <div className="flex items-start gap-2">
        <span className="text-xl">✓</span>
        <div className="flex-1">
          <h4 className="font-semibold text-gray-900">{result.message || 'Action completed'}</h4>
          {result.summary && <p className="text-sm text-gray-600 mt-1">{result.summary}</p>}
          {result.note && <p className="text-xs text-gray-500 mt-2 italic">{result.note}</p>}
        </div>
      </div>
      {metadata}
      {actionButtons}
    </div>
  );
};

export default ToolResultCard;
